import React from "react";
import moment from "moment";
import { TextField, Grid } from "@mui/material";
import NumberFormat from "react-number-format";
// Replacing Column with MUI Grid items
import { RangeFilterSetting, useSidebarFilters } from "../../sidebar-filters";
import { FilterName, FilterProps } from ".";

const getDisplayedValue =
(isMin: boolean, currentRange: RangeFilterSetting, year?: boolean): string => {
  if (year) {
    const timestamp = isMin ? currentRange.max : currentRange.min;
    return timestamp ? `${moment().diff(moment.unix(Number(timestamp)), "years")}` : "";
  }
  const value = isMin ? currentRange.min : currentRange.max;
  return value ? String(value) : "";
};

type FilterRangeProps = Omit<FilterProps, "name"> & {
  year?: boolean;
  containerStyle?: React.CSSProperties;
  name: string;
}

const NumericInput = (props: {
  label: string;
  name: string;
  value: string | number;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) => <TextField type="number" slotProps={{ htmlInput: { min: "0" } }} {...props} />;

const CurrencyInputField = (props: {
  label: string;
  name: string;
  value: string | number;
  onValueChange: (value: string) => void;
}) => (
  <NumberFormat
    label={props.label}
    name={props.name}
    value={props.value === "" ? "" : String(props.value)}
    isNumericString
    thousandSeparator
    decimalScale={0}
    prefix={"$"}
    customInput={TextField}
    onChange={() => null}
    onValueChange={({ value }: { value: string }) => props.onValueChange(value)}
  />
);

export const FilterRange: React.FC<FilterRangeProps> = (props) => {
  const { name, year } = props;
  const minName = `${name}_min` as FilterName;
  const maxName = `${name}_max` as FilterName;
  const { addFilter, removeFilter, filterState } = useSidebarFilters();
  const currentRange = (filterState[name]?.value as RangeFilterSetting) || {};
  const minVal = getDisplayedValue(true, currentRange, year);
  const maxVal = getDisplayedValue(false, currentRange, year);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, type: "min" | "max") => {
    const newValue = event.target.value;
    const currentMin = type === "min" ? newValue : minVal;
    const currentMax = type === "max" ? newValue : maxVal;

    removeFilter(name);
    const applyFilterAndRefinement = (filterName: string, value: string, isMin: boolean) => {
      let minMax = isMin;
      let actualValue = parseInt(value, 10);
      if (year) {
        actualValue = moment().subtract(value, "years").unix();
        minMax = !minMax;
      }
      const suffix = minMax ? "Min" : "Max";

      if (Number.isNaN(actualValue)) {
        removeFilter(filterName, `range${suffix}`);
      } else {
        addFilter(filterName, `${actualValue}`, `range${suffix}`);
      }
    };

    if (currentMin) applyFilterAndRefinement(name, currentMin, true);
    if (currentMax) applyFilterAndRefinement(name, currentMax, false);
  };

  if (!props.filters.includes(props.name as FilterName)) return null;
  return (
    <Grid container spacing={2}
      style={{ ...props.containerStyle }}>
      <Grid size={{ xs: 6 }}>
        <NumericInput label="Min" name={minName} value={minVal} onChange={(event) => handleChange(event, "min")} />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <NumericInput label="Max" name={maxName} value={maxVal} onChange={(event) => handleChange(event, "max")} />
      </Grid>
    </Grid>
  );
};

export const IncomeFilter: React.FC<Omit<FilterRangeProps, "name"> & {name: string}> = (props) => {
  const { name } = props;
  const minName = `${name}_min` as FilterName;
  const maxName = `${name}_max` as FilterName;
  const { addFilter, removeFilter, filterState } = useSidebarFilters();
  const range = (filterState[name]?.value as RangeFilterSetting) || {};
  const minVal = range.min ?? "";
  const maxVal = range.max ?? "";

  if (!props.filters.includes(props.name as FilterName)) return null;
  return (
    <Grid container spacing={2}
      style={{ ...props.containerStyle }}>
      <Grid size={{ xs: 6 }}>
        <CurrencyInputField
          label="Min"
          name={minName}
          value={minVal}
          onValueChange={(value) => {
            const nextMin = value;
            const nextMax = maxVal;
            removeFilter(name);
            if (nextMin) addFilter(name, nextMin, "rangeMin");
            if (nextMax) addFilter(name, nextMax, "rangeMax");
          }}
        />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <CurrencyInputField
          label="Max"
          name={maxName}
          value={maxVal}
          onValueChange={(value) => {
            const nextMin = minVal;
            const nextMax = value;
            removeFilter(name);
            if (nextMin) addFilter(name, nextMin, "rangeMin");
            if (nextMax) addFilter(name, nextMax, "rangeMax");
          }}
        />
      </Grid>
    </Grid>
  );
};
